// Las fotos SUBIDAS de Recrear — UNA fuente para las dos pantallas (`InterfazV2Plantillas`
// y `PlantillasBelleza`), ADR-2026-10-05-recrear-con-todas-subidas-y-subir-imagen.
//
// Recrear ofrece dos puntos de partida: una plantilla del catálogo o una foto propia. La
// foto propia es la colección que ya tenía el modo "Recrear imagen" (`plantillas_propias`,
// `plantillasPropiasApi`): lo que se sube acá aparece allá y al revés. Subir una —o tocar
// una ya subida— lleva al modo con esa foto elegida.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import { AbanicoDeFotos } from '../../../components/AbanicoDeFotos';
import { UnsupportedImageFormatError } from '../../../utils/heicConvert';
import CachedImg from '../../../components/CachedImg';
import { empresaRecordada, recordarEmpresa } from '../empresaDeLaSesion';
import {
  fetchPlantillasPropias,
  subirPlantillaPropia,
  type PlantillaPropia,
} from '../plantillasPropiasApi';

/** El id del chip "Subidas". Viaja en `?categoria=` como cualquier otra categoría. */
export const CATEGORIA_SUBIDAS = 'subidas';

/** La ruta del modo "Recrear imagen" (`modesCatalog`, path `replicar-foto`). */
export const RUTA_RECREAR_IMAGEN = '/replicar-foto';

/**
 * Un pin de Pinterest elegido en Recrear (`pinterestParaRecrear`): sólo sus links de
 * i.pinimg.com, que no se guardan (ADR-2026-10-29-pinterest-en-recrear-con-plantillas).
 */
export interface PinParaRecrear {
  /** La imagen grande: la que se manda a generar. */
  imagen: string;
  /** La de 600 de ancho: la que se muestra. */
  miniatura: string;
  titulo: string | null;
}

/** Lo que Recrear le pasa al modo por `location.state` para que abra con la foto elegida. */
export interface EstadoDeRecrearImagen {
  plantillaId?: string;
  /** Un pin de Pinterest en vez de una subida. */
  pin?: PinParaRecrear;
}

/**
 * Las subidas de la empresa y las dos acciones de Recrear sobre ellas: subir una nueva y
 * abrir el modo con una elegida. `inputDeArchivo` hay que renderizarlo (una vez) en la
 * pantalla: es el `<input type="file">` oculto que abre `abrirSelector`.
 */
export function useSubidasParaRecrear(brandId: string | null) {
  const navigate = useNavigate();
  const [companyId, setCompanyId] = useState<string | null>(() => empresaRecordada());
  const [subidas, setSubidas] = useState<PlantillaPropia[]>([]);
  const [subiendo, setSubiendo] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    let cancelado = false;
    (async () => {
      const { data: auth } = await supabase.auth.getUser();
      if (!auth.user || cancelado) return;
      const { data: p } = await supabase
        .from('profiles')
        .select('id_company')
        .eq('id', auth.user.id)
        .maybeSingle();
      if (cancelado) return;
      const idCompany = (p as { id_company?: string | null } | null)?.id_company ?? null;
      setCompanyId(idCompany);
      recordarEmpresa(idCompany, auth.user.id);
    })();
    return () => {
      cancelado = true;
    };
  }, []);

  useEffect(() => {
    if (!companyId) return;
    let cancelado = false;
    fetchPlantillasPropias(companyId)
      .then((lista) => {
        if (!cancelado) setSubidas(lista);
      })
      .catch(() => {
        /* sin subidas la pantalla sigue ofreciendo subir */
      });
    return () => {
      cancelado = true;
    };
  }, [companyId]);

  const abrirSubida = useCallback(
    (p: PlantillaPropia) => {
      const state: EstadoDeRecrearImagen = { plantillaId: p.id };
      navigate(RUTA_RECREAR_IMAGEN, { state });
    },
    [navigate],
  );

  const abrirSelector = useCallback(() => {
    if (!subiendo) inputRef.current?.click();
  }, [subiendo]);

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // permite volver a elegir el MISMO archivo
    if (!file || !companyId) return;
    setSubiendo(true);
    setError(null);
    try {
      const nueva = await subirPlantillaPropia({
        file,
        idCompany: companyId,
        brandId,
        cantidadActual: subidas.length,
      });
      setSubidas((prev) => [nueva, ...prev]);
      abrirSubida(nueva);
    } catch (err) {
      console.error('[subidasParaRecrear] subir imagen falló', err);
      setError(
        err instanceof UnsupportedImageFormatError
          ? err.message
          : 'No se pudo subir la imagen. Intentá nuevamente.',
      );
    } finally {
      setSubiendo(false);
    }
  };

  const inputDeArchivo = (
    <input
      ref={inputRef}
      type="file"
      accept="image/*"
      className="hidden"
      onChange={onFile}
    />
  );

  return { subidas, subiendo, error, abrirSelector, abrirSubida, inputDeArchivo };
}

/**
 * La card fija del principio de la grilla: subir una foto propia para recrearla. Tiene la
 * cara de "Subir nuevo producto" de los modos (punteado celeste, degradé y el abanico de
 * fotos, que se abre en hover) más un título y una bajada que cuentan qué pasa con la foto.
 */
export const CardSubirParaRecrear: React.FC<{
  aspectClass: string;
  subiendo: boolean;
  error: string | null;
  onClick: () => void;
}> = ({ aspectClass, subiendo, error, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={subiendo}
    className={`group w-full mb-3 sm:mb-4 break-inside-avoid rounded-xl relative overflow-hidden flex flex-col items-center justify-center gap-4 px-5 text-center border-2 border-dashed border-[rgb(var(--brand-primary-rgb)_/_0.215)] bg-gradient-to-b from-white to-[rgb(var(--brand-primary-rgb)_/_0.055)] transition-[border-color,box-shadow,transform] duration-300 hover:border-brand-primary hover:shadow-[0_12px_32px_-14px_rgb(var(--brand-primary-rgb)_/_0.45)] active:scale-[0.99] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary disabled:pointer-events-none ${aspectClass}`}
  >
    <span
      aria-hidden
      className="pointer-events-none absolute inset-x-0 top-0 h-2/3 bg-[radial-gradient(60%_70%_at_50%_0%,rgb(var(--brand-primary-rgb)_/_0.10),transparent)] opacity-70 transition-opacity duration-300 group-hover:opacity-100"
    />
    <span className="relative sm:scale-125">
      <AbanicoDeFotos cargando={subiendo} />
    </span>
    {/* El `scale-125` del abanico no ocupa lugar en el flujo: el `mt` le devuelve el aire. */}
    <span className="relative flex flex-col items-center gap-1.5 sm:mt-4">
      <span className="text-[14px] sm:text-[16px] font-semibold leading-tight text-[#16181D]">
        {subiendo ? 'Subiendo…' : 'Recreá tu propia foto'}
      </span>
      <span className="max-w-[240px] text-[12.5px] sm:text-[13.5px] leading-snug text-[#6B7080] [text-wrap:balance]">
        Subí una imagen que te guste y Atlas la vuelve a hacer con tus productos.
      </span>
    </span>
    {error && !subiendo && (
      <span className="relative text-[12px] leading-snug text-red-600">{error}</span>
    )}
  </button>
);

/** Una foto subida, con la misma caja que una plantilla de la grilla. */
export const CardSubida: React.FC<{
  subida: PlantillaPropia;
  aspectClass: string;
  onClick: () => void;
}> = ({ subida, aspectClass, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`w-full mb-3 sm:mb-4 break-inside-avoid rounded-xl overflow-hidden bg-neutral-100 relative group active:scale-[0.99] transition-transform block ${aspectClass}`}
  >
    <CachedImg
      src={subida.imageUrl}
      alt={subida.nombre}
      loading="lazy"
      className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500 ease-out"
    />
  </button>
);
